/* screens/crm/Board.jsx — канбан-доска воронки.
   Desktop: колонки=этапы, карточки=лиды, DnD между этапами (на drop → модалка
   обязательной причины). Mobile: один этап за раз (селектор этапов сверху) +
   вертикальный список карточек, перемещение — через карточку лида.
   Выбор лидов (чекбокс на карточке) включает массовые действия (родитель). */
import React from 'react';
import { Avatar } from '../../components/ui/Avatar.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { fmt } from '../../lib/format.js';

const { useState } = React;

function dotClass(kind) {
  if (kind === 'won') return 'crm-col__dot crm-col__dot--won';
  if (kind === 'lost') return 'crm-col__dot crm-col__dot--lost';
  return 'crm-col__dot';
}
function sumValue(leads) {
  let s = 0;
  for (const l of leads) { const v = parseFloat(l.value); if (!Number.isNaN(v)) s += v; }
  return s;
}
function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function LeadCard({ lead, selected, onToggleSelect, onOpen, draggable, onDragStart, onDragEnd, dragging }) {
  const stale = daysSince(lead.last_touch_at);
  return (
    <div
      className={'crm-card' + (dragging ? ' is-dragging' : '') + (selected ? ' is-selected' : '')}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(lead)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onOpen(lead); } }}
    >
      <label className="crm-card__check" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={selected} onChange={() => onToggleSelect(lead.id)} aria-label="Выбрать лид" />
      </label>
      <div className="crm-card__title">{lead.client}</div>
      {lead.company && (
        <div className="crm-card__sub truncate">{lead.company}</div>
      )}
      <div className="crm-card__row">
        {lead.value != null && <span className="crm-card__value num">{fmt.money(lead.value)}</span>}
        {lead.source_label && <span className="crm-card__sub">{lead.source_label}</span>}
        {lead.note_count != null && lead.note_count > 0 && (
          <span className="crm-card__notecount"><Icon name="file-text" size={13} />{lead.note_count}</span>
        )}
        {stale != null && stale >= 3 && (
          <span className="crm-card__stale" title="Дней без активности">
            <Icon name="clock" size={12} />{stale}д
          </span>
        )}
        <span className="crm-card__owner">
          {lead.owner_label
            ? <Avatar src={lead.owner_avatar} label={lead.owner_label} size={22} />
            : <span className="text-mute" style={{ fontSize: 12 }}>без ответственного</span>}
        </span>
      </div>
    </div>
  );
}

function ColumnHead({ stage, count, total, onAddLead }) {
  return (
    <>
      <div className="crm-col__head">
        <span className={dotClass(stage.kind)} aria-hidden="true" />
        <span className="crm-col__name" title={stage.name}>{stage.name}</span>
        <span className="crm-col__count num">{count}</span>
        <button type="button" className="icon-btn-mini" aria-label={'Добавить лид в «' + stage.name + '»'}
          title="Добавить лид" onClick={() => onAddLead(stage)}>
          <Icon name="plus" size={15} />
        </button>
      </div>
      {total > 0 && <div className="crm-col__sum num" title="Сумма лидов на этапе">Σ {fmt.money(total)}</div>}
    </>
  );
}

// ── Мобильная доска: один этап + селектор ────────────────────────────────────
function MobileBoard({ columns, selectedIds, onToggleSelect, onOpenLead, onAddLead }) {
  const [stageId, setStageId] = useState(columns[0] ? columns[0].stage.id : null);
  const active = columns.find((c) => c.stage.id === stageId) || columns[0];
  if (!active) return null;
  return (
    <div className="crm-mboard">
      <div className="crm-mboard__tabs scroll-row">
        {columns.map(({ stage, leads }) => (
          <button key={stage.id} type="button"
            className={'crm-mtab' + (stage.id === active.stage.id ? ' is-active' : '')}
            onClick={() => setStageId(stage.id)}>
            <span className={dotClass(stage.kind)} aria-hidden="true" />
            {stage.name}<span className="crm-mtab__count num">{leads.length}</span>
          </button>
        ))}
      </div>
      <div className="crm-mboard__head">
        <span className="crm-col__name">{active.stage.name}</span>
        {sumValue(active.leads) > 0 && <span className="crm-col__sum num">Σ {fmt.money(sumValue(active.leads))}</span>}
        <div className="grow" />
        <button type="button" className="icon-btn-mini" aria-label="Добавить лид"
          onClick={() => onAddLead(active.stage)}><Icon name="plus" size={16} /></button>
      </div>
      <div className="crm-mboard__list">
        {active.leads.length === 0
          ? <div className="crm-col__empty">Нет лидов</div>
          : active.leads.map((lead) => (
            <LeadCard key={lead.id} lead={lead} selected={selectedIds.has(lead.id)}
              onToggleSelect={onToggleSelect} onOpen={onOpenLead} draggable={false} />
          ))}
      </div>
    </div>
  );
}

function Board({ columns, mobile, selectedIds, onToggleSelect, onOpenLead, onRequestMove, onAddLead }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  if (mobile) {
    return (
      <MobileBoard columns={columns} selectedIds={selectedIds} onToggleSelect={onToggleSelect}
        onOpenLead={onOpenLead} onAddLead={onAddLead} />
    );
  }

  const stageByLead = {};
  const leadById = {};
  columns.forEach((c) => c.leads.forEach((l) => { stageByLead[l.id] = c.stage.id; leadById[l.id] = l; }));

  const start = (lead) => (e) => {
    setDragId(lead.id);
    try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(lead.id)); } catch (_) { /* noop */ }
  };
  const end = () => { setDragId(null); setOverCol(null); };
  const allowDrop = (e) => { e.preventDefault(); try { e.dataTransfer.dropEffect = 'move'; } catch (_) { /* noop */ } };
  const overColumn = (stageId) => (e) => { allowDrop(e); if (overCol !== stageId) setOverCol(stageId); };
  const dropColumn = (stage) => (e) => {
    e.preventDefault();
    const id = dragId;
    setDragId(null); setOverCol(null);
    if (id != null && stageByLead[id] !== stage.id && leadById[id]) onRequestMove(leadById[id], stage);
  };

  return (
    <div className="crm-board">
      {columns.map(({ stage, leads }) => (
        <div key={stage.id}
          className={'crm-col' + (overCol === stage.id ? ' is-over' : '')}
          onDragOver={overColumn(stage.id)} onDrop={dropColumn(stage)}>
          <ColumnHead stage={stage} count={leads.length} total={sumValue(leads)} onAddLead={onAddLead} />
          <div className="crm-col__body">
            {leads.length === 0 && <div className="crm-col__empty">Перетащите лид сюда</div>}
            {leads.map((lead) => (
              <LeadCard key={lead.id} lead={lead} selected={selectedIds.has(lead.id)}
                onToggleSelect={onToggleSelect} onOpen={onOpenLead}
                draggable onDragStart={start(lead)} onDragEnd={end} dragging={dragId === lead.id} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export { Board };
